import { read, TABLE } from '../data/db';
import { coachCards, matches, teams } from '../data/repos';
import { byMatchHistory, matchStateLabel } from '../domain/file';
import {
  deleteEventForB,
  isMatchHistoryB,
  matchPurposeLabel,
  needsFinishingB,
  openTargetForB,
} from '../domain/homeB';
import type { DeleteEventB, OpenTargetB } from '../domain/homeB';
import type { MatchFormat, MatchRow } from '../types';
import { dispatch } from './bus';
import type { DispatchResult } from './bus';
import { discardSetup } from './preMatch';
import { isResumable } from './resume';

/**
 * B spec §4.5 — **the Matches home read** (build unit B-5): everything the tab shell's home
 * surface renders, from one readonly transaction. The A convention (`app/home.ts`), unchanged:
 * one snapshot, so the In-progress card, the Needs-finishing queue and the history list are
 * three views of the same `matches` read and can never disagree about a match — a queue read
 * after a history read could list a match the rows above it do not include.
 *
 * ── Why this is not `loadHome` widened ───────────────────────────────────────
 *
 * A's `loadHome` serves A's Home: the team list and the two distillation groups. B's home is a
 * different composition — it joins **`coachCards`**, a table A's reads never see (§2.2), keys
 * its queue on F-1's ruling rather than on distillation, and renders full match history on the
 * home surface (A renders history per-team). Widening A's read would put B's queue semantics
 * under A's screen; the reuse contract's clause 4 keeps A untouched instead. The *decisions*
 * are shared where they genuinely coincide: `byMatchHistory`, `matchStateLabel` and
 * `isResumable` are imported, not re-derived.
 *
 * ── The transaction SCOPE is a mechanism (the `loadHome` discipline) ─────────
 *
 * Three tables: `matches`, `teams` (opponent names), `coachCards` (the Needs-finishing join).
 * `observations`, `hypotheses`, `annotations`, `flags` and every audio table are **absent from
 * the scope**, and Dexie makes a read of them throw: the Matches tab is the natural home for
 * "and while we're here, show the counts", and every such widening turns a launch surface into
 * a capture log. The widening argument happens at review time, not in a release.
 *
 * **Not on any capture path.** Home is reached out-of-match (§2.2's shell boundary), so reading
 * every match row is the honest way to compose the three groups without denormalised counters.
 */

const MODULE = 'app/homeB';

/** One match row on the Matches home, in any of the three groups (§2.4's row anatomy). */
export interface HomeBMatch {
  readonly matchId: string;
  readonly teamId: string;
  readonly opponent: string;
  readonly date: string;
  readonly comp: string | null;
  /** §2.4 — the format chip. `null` (setup never finished) renders no chip. */
  readonly format: MatchFormat | null;
  /** §2.4 — the purpose chip's copy, resolved in `domain/` (one vocabulary). `null` — no chip. */
  readonly purposeLabel: string | null;
  readonly stateLabel: string;
  readonly abandoned: boolean;
  /** True iff a draft `CoachCardRow` exists for this match — the §2.2 queue's first arm. */
  readonly holdsDraftCard: boolean;
  /**
   * Which transition would delete this row, or `null` — `domain/homeB.deleteEventForB` (Andrew's
   * ruling, 2026-08-19). Resolved here for `stateLabel`'s reason: `MatchState` values are spelled
   * where the vocabulary is owned, and the surface never sees one. What the surface *does* see is
   * this narrowed event, which it carries back to {@link deleteMatch} without interpreting it —
   * so the two populations the ruling names stay one decision, made in `domain/`.
   */
  readonly deleteEvent: DeleteEventB | null;
  /**
   * Where the In-progress card's tap opens — `domain/homeB.openTargetForB` — or `null` for a match
   * that is not on that card at all (every queue and history row). Resolved here for
   * `stateLabel`'s and `deleteEvent`'s reason: the surface never sees a `MatchState`, it sees the
   * navigation the state implies, and the deciding is done once in `domain/`.
   *
   * **`null` on the In-progress card is unconstructible, not merely unlikely**: that group's
   * membership is `isResumable`, and `homeB.test.ts` asserts `openTargetForB(state) !== null` is
   * that same predicate over every state in both directions.
   */
  readonly openTarget: OpenTargetB | null;
}

export interface HomeBView {
  /** §2.4 — the In-progress card's matches: `preMatch` ∨ in-match. ≥2 only on damaged data. */
  readonly inProgress: readonly HomeBMatch[];
  /** §2.2 — the Needs-finishing queue: draft-card holders ∪ `fullTime` (F-1 approved). */
  readonly needsFinishing: readonly HomeBMatch[];
  /** Scenario 33 — every post-match row, `byMatchHistory` (newest first). Superset of the queue. */
  readonly history: readonly HomeBMatch[];
}

/**
 * Everything the Matches home renders, from one readonly transaction.
 *
 * @throws {StorageError} on a read failure. Not swallowed: an empty home and an unreadable home
 *   look identical, and only one of them is the ordinary state of a fresh install (the
 *   `loadHome` rule; B's first run is exactly that fresh install).
 */
export async function loadHomeB(): Promise<HomeBView> {
  return read(
    [TABLE.matches, TABLE.teams, TABLE.coachCards],
    async (): Promise<HomeBView> => {
      const [matchRows, teamRows, draftCards] = await Promise.all([
        matches.listAllMatches(),
        teams.listTeams(),
        coachCards.listDraftCoachCards(),
      ]);

      const nameOf = new Map(teamRows.map((row) => [row.id, row.name]));
      const draftHolders = new Set(draftCards.map((card) => card.matchId));

      const projected = (row: MatchRow): HomeBMatch => ({
        matchId: row.id,
        teamId: row.teamId,
        // The fallback mirrors `loadHome`'s: a cascade cannot orphan a match (INV-8), so this is
        // reachable only from a partially-restored archive, and a home that refused to render
        // because one row lost its team would take the whole tab down with it.
        opponent: nameOf.get(row.teamId) ?? 'Opponent',
        date: row.date,
        comp: row.comp,
        format: row.format,
        purposeLabel: matchPurposeLabel(row.purpose),
        stateLabel: matchStateLabel(row),
        abandoned: row.abandoned,
        holdsDraftCard: draftHolders.has(row.id),
        deleteEvent: deleteEventForB(row.state, row.abandoned),
        openTarget: openTargetForB(row.state),
      });

      // One order for all three groups: `byMatchHistory` is newest-first and total, so the same
      // two matches never render in a different order on different launches.
      const inOrder = [...matchRows].sort(byMatchHistory);
      const group = (keep: (row: MatchRow) => boolean): HomeBMatch[] =>
        inOrder.filter(keep).map(projected);

      return {
        inProgress: group(isResumable),
        needsFinishing: group((row) => needsFinishingB(row.state, draftHolders.has(row.id))),
        history: group((row) => isMatchHistoryB(row.state)),
      };
    },
    MODULE,
  );
}

/**
 * **Delete a match from the Matches home** (Andrew, 2026-08-19) — the screen's one lifecycle move,
 * `app/matchRecord.completeMatch`'s shape, one screen over.
 *
 * ── One entry point, two existing events, and no new dispatch for the second ──
 *
 * The ruling names two populations, and the engine already had a transition for each: `DeleteMatch`
 * for an abandoned match (the addition this unit was authorised to make) and `DiscardSetup` for an
 * unstarted one (since U-4). The `DiscardSetup` arm therefore **calls `app/preMatch.discardSetup`
 * rather than spelling a second dispatch of its own** — A's Pre-Match screen has driven that facade
 * for the whole project, and a parallel copy here would be two producers of one effect
 * (`new-path-parity-single-source-the-effect`). Below this line both arms are the same
 * `to: null` → `cascadeDeleteMatch` path: one deletion, one cascade.
 *
 * @param via which transition — from `HomeBMatch.deleteEvent`, decided in `domain/homeB.ts`. The
 *   caller cannot get this wrong in a way that matters: `machine.transition` refuses the wrong
 *   event for the state (`ERR_ILLEGAL_TRANSITION`) and refuses `DeleteMatch` on a match that was
 *   played (`ERR_MATCH_NOT_ABANDONED`), both from the row read **inside** the bus's transaction.
 *   So a home rendered before another surface moved the match is refused rather than obeyed, which
 *   is what makes the row's control a courtesy and not the guarantee.
 *
 * INV-8's cascade does the rest in that same transaction — every `MATCH_SCOPED_TABLES` row and the
 * match's audio blobs, and deliberately **not** `ourPlayers`, which is profile-scoped and survives
 * every match delete. No network is touched: ENV-9 aside, a delete that waited on a request would
 * be a delete that fails when the phone has no signal.
 */
export function deleteMatch(matchId: string, via: DeleteEventB): Promise<DispatchResult> {
  if (via === 'DiscardSetup') return discardSetup(matchId);
  return dispatch({ type: 'Transition', event: 'DeleteMatch', matchId });
}
